<template>
  <div class="text-facet">
    <div
      v-if="facet || enableAdditionalToggles"
      class="text-facet__container"
    >
      <h3
        class="visually-hidden"
        v-text="facet?.label"
      />

      <div class="text-facet__values">
        <div
          v-if="enableAdditionalToggles"
          class="text-facet__value text-facet__value--no-input"
        >
          <div class="text-facet__label">
            <button
              type="button"
              name=""
              class="text-facet__label-text text-body-2xs-regular button button--text"
              @click.prevent="openFacetDrawer('color')"
              v-text="$string(
                'collection.filters.color_toggle')"
            />
          </div>
        </div>

        <div
          v-if="enableAdditionalToggles"
          class="text-facet__value text-facet__value--no-input"
        >
          <div class="text-facet__label">
            <button
              type="button"
              class="text-facet__label-text text-body-2xs-regular button button--text"
              @click.prevent="openFacetDrawer('size')"
              v-text="$string(
                'collection.filters.size_toggle')"
            />
          </div>
        </div>

        <template
          v-for="(facetValue, i) of allFacetValues"
          :key="`${facetValue.value}-${facetValue.active}`"
        >
          <div class="text-facet__value">
            <input
              :id="`text-facet__input--${i}`"
              type="checkbox"
              class="text-facet__input visually-hidden"
              :aria-label="(
                isFacetActive(facetValue)
              ) ? $string(
                'collection.filters.deselect_value_accessibility_text', {
                  replace: {
                    value: getFacetLabel(facetValue) + ' - ' + facetValue.label
                  }
                }
              ) : $string(
                'collection.filters.select_value_accessibility_text', {
                  replace: {
                    value: getFacetLabel(facetValue) + ' - ' + facetValue.label
                  }
                }
              )"
              :name="facet.field"
              :value="facetValue.value"
              :checked="isFacetActive(facetValue)"
              :disabled="isLoading"
              @change.prevent="
                (event) => updateSelectedFacetValue(
                  getFacetField(facetValue),
                  facetValue.value,
                  event.target.checked
                )
              "
            >

            <label
              :for="`text-facet__input--${i}`"
              class="text-facet__label"
            >
              <h4
                class="text-facet__label-text text-body-2xs-regular"
                v-text="facetValue.label"
              />
            </label>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Text facet (text-facet)
 * -----------------------------------------------------------------------------
 * A text facet for SearchSpring filtering.
 * @param {Object} facet - The facet data.
 * @param {Boolean} isLoading - Whether the SS data is loading.
 *
 */

export default {
  name: 'TextFacet',

  props: {
    facet: {
      type: Object,
      default: null,
    },

    isLoading: {
      type: Boolean,
      required: true,
    },

    enableAdditionalToggles: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'facet-value-changed': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      draftFacetUpdate: null,
    }
  },

  computed: {
    allFacetValues() {
      if (!Array.isArray(this.facet)) {
        return this.facet?.values
      }
      return this.facet?.map(fc => fc?.values || []).flat().filter(Boolean)
    },
  },

  watch: {
    facet() {
      this.draftFacetUpdate = null
    },
  },

  methods: {

    /**
     * Opens modal drawer for facets
     * @param {String} target - The target to open
     */
    openFacetDrawer(target) {
      cnvs.EventBus.emit('overlay:open-facet-drawer', target)
    },

    /**
     * Returns if the facet is active
     * @param {Object} facetValue - The facet value
     * @returns {Boolean} Final facets
     */
    isFacetActive(facetValue) {
      return facetValue.active || (this.isLoading && this.draftFacetUpdate === facetValue.value)
    },

    /**
     * Emits an update event for the parent component to update its selections
     */
    updateSelectedFacetValue(field, value, selected) {
      this.draftFacetUpdate = value
      this.$emit('facet-value-changed', {
        field,
        value,
        selected,
      })
    },

    /**
     * Fetches the facet label
     */
    getFacetLabel(facetValue) {
      if (Array.isArray(this.facet) && this.facet.length > 0) {
        const rawFacet = this.facet.find(fc => fc.values.some(
          val => val.value === facetValue.value,
        ))
        return rawFacet?.label
      }
      return this.facet.label
    },

    /**
     * Fetches the facet field value
     */
    getFacetField(facetValue) {
      if (Array.isArray(this.facet) && this.facet.length > 0) {
        const rawFacet = this.facet.find(fc => fc.values.some(
          val => val.value === facetValue.value,
        ))
        return rawFacet?.field
      }
      return this.facet.field
    },
  },
}
</script>

<style lang="scss">
@import './text-facet';
</style>
